import { getSecrets } from '../../../utils/getSecrets';
import { identityApiRef } from '@backstage/core-plugin-api';
import { Secret } from '../../../types/secret';
import { Team } from '@internal/schemas-common';
import useAsync from 'react-use/esm/useAsync';

import { discoveryApiRef, useApi } from '@backstage/core-plugin-api';
import { useEffect, useRef } from 'react';

type UseGetSecretsProps = {
  teams?: Team[];
  useFullArrayAsDependency?: boolean;
};
type UseGetSecretsResult = {
  value: Secret[] | undefined;
  loading: boolean;
  error: Error | undefined;
};

export const useGetSecrets = ({
  teams,
}: UseGetSecretsProps): UseGetSecretsResult => {
  const proxyBaseurl = useRef('');
  const token = useRef('');
  const secrets = useRef<Secret[]>([]);
  const apiRef = useApi(discoveryApiRef);
  const identityRef = useApi(identityApiRef);
  const teamsIdentityKey = JSON.stringify(teams);

  useEffect(() => {
    identityRef.getCredentials().then(cred => {
      if (cred?.token) {
        token.current = cred.token;
      }
    });
  }, [identityRef]);

  // Get proxy URL
  useEffect(() => {
    apiRef.getBaseUrl('aws-services').then(baseUrl => {
      if (baseUrl) {
        proxyBaseurl.current = baseUrl;
      }
    });
  }, [apiRef]);

  const { value, loading, error } = useAsync(async () => {
    const teamNames =
      !teams || teams.length === 0
        ? ['ALL']
        : teams
            .slice()
            .sort((a, b) => a.nameInGitlab.localeCompare(b.nameInGitlab))
            .map(t => t.nameInGitlab);

    const _secrets = await Promise.allSettled<Secret[]>(
      teamNames.map(teamName =>
        getSecrets({
          baseUrl: proxyBaseurl.current,
          token: token.current,
          teamName,
        }),
      ),
    );

    // TODO: Log failures
    return _secrets
      .filter(_secret => _secret?.status === 'fulfilled')
      .map(_secret => (_secret as PromiseFulfilledResult<Secret[]>).value)
      .flat();
  }, [proxyBaseurl.current, token.current, teamsIdentityKey]);

  if (value) {
    secrets.current = value as Secret[];
  }

  return { value: secrets.current, loading, error };
};
